import FileUpload from "components/FileUpload";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { addSuspectToWitness } from "services/caseServices";
import { fetchSuspect } from "services/lealIntityService";
import { InvestigationFile, Suspect, SuspectTowitness } from "types";
import { LoadingIcon } from "utils/constant";
import { errorHandlerOnForm } from "utils/helper";

const defaultValue: SuspectTowitness = {
  crime_registration_number: "",
  incident_registration_number: "",
  prosecutor_registration_number: "",
  suspect: "",
  agreement_with_suspect: false,
  agreement_with_suspect_file: null,
  request_date: null,
  decision: "Pending",
  conversion_summary: "",
  immunity_revoked: false,
  revocation_reason: "",
  revoked_by_attorney_general: false,
  prosecutor_name: "",
  prosecutor_user_id: "",
  org_unit_id: "",
  admin_unit_id: "",
  status: "Pending",
  revocation_decision: null,
};

interface DoubtfulRequestProps {
  file: InvestigationFile;
  onCancel: () => void;
}

const RequestSuspectToWitnessModal: React.FC<DoubtfulRequestProps> = ({
  file,
  onCancel,
}) => {
  // const { state } = useLocation();
  const navigate = useNavigate();
  const { loginData } = useAuth();
  const { showTooltip } = useTooltip();
  const [loading, setLoading] = React.useState(false);

  const [formState, setFormState] = useState<SuspectTowitness>(defaultValue);
  const [suspects, setSuspects] = useState<Suspect[]>([]);

  useEffect(() => {
    if (file) {
      setFormState((prevState) => ({
        ...prevState,
        crime_registration_number: file?.crime_reference_number,
        incident_registration_number: file?.incident_registration_number,
      }));
    }
  }, [file]);
  useEffect(() => {
    if (file) {
      setLoading(true);
      fetchSuspect()
        .then((data) => {
          setSuspects(
            data?.filter(
              (s) =>
                s?.Incident_Registration_number ===
                file?.incident_registration_number
            )
          );
        })
        .catch(console.error)
        .finally(() => setLoading(false));
    }
  }, [file]);

  useEffect(() => {
    if (loginData) {
      setFormState((prevState) => ({
        ...prevState,
        prosecutor_user_id: String(loginData?.user_id ?? ""),
        org_unit_id: String(loginData?.org_unit ?? ""),
        admin_unit_id: String(loginData?.admin_unit ?? ""),
        prosecutor_name: String(loginData?.full_name ?? ""),
      }));
    }
  }, [loginData]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const validateForm = () => {
    if (!formState?.suspect) return "Suspect is Required";
    else if (!formState.agreement_with_suspect_file)
      return "Agreement With Suspect File is Required.";
    return "";
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const error = validateForm();
    if (error) {
      showTooltip(error, "error");
      return;
    }
    const formData: SuspectTowitness = {
      ...formState,
      decision: "Pending",
      status: "Pending",
      agreement_with_suspect: true,
    };

    addSuspectToWitness(formData)
      .then(() => {
        showTooltip("Successfully Submitted!", "success");
        navigate("/suspect-to-witness");
      })
      .catch((err) => {
        showTooltip(errorHandlerOnForm(err?.response?.data), "error");
      });
  };

  const handleClickOutside = (e: any) => {
    if (e.target.classList.contains("fixed")) {
      onCancel();
    }
  };

  const handleFile = (name: string, file: File | null) => {
    setFormState({ ...formState, [name]: file });
  };

  return (
    <div
      style={{ marginTop: "0px" }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4"
      onClick={handleClickOutside}
    >
      <div className="bg-white rounded-lg shadow-lg p-6 w-[600px]">
        <h2 className="text-lg font-semibold text-gray-800">
          Suspect to Witness
        </h2>
        <form onSubmit={handleSubmit}>
          <div className="rounded-lg  mt-4 ">
            {/* Suspect Information */}
            <div>
              <div>
                {/* Select Suspect (first) */}
                <div className="flex flex-col mt-4 ">
                  <label className="text-sm text-[#747171] font-[inter] font-[400] flex-col-2 mb-2">
                    Select Suspect
                  </label>
                  <select
                    className="border border-gray-300 rounded-lg bg-[#F2F2F2] p-2 pr-8 focus:outline-none focus:ring focus:ring-blue-300"
                    value={formState?.suspect ?? ""}
                    name="suspect"
                    onChange={(e) => {
                      const selectedId = e.target.value ? e.target.value : "";
                      // const selectedSuspect =
                      //   suspects.find(
                      //     (s) => String(s.uu_id) === String(selectedId)
                      //   ) || null;

                      setFormState({
                        ...formState,
                        suspect: selectedId,
                      });
                    }}
                  >
                    <option value={""}>Select Suspect</option>
                    {suspects.map((suspect, index) => (
                      <option key={index} value={suspect?.uu_id}>
                        {suspect?.first_name} {suspect?.middle_name}{" "}
                        {suspect?.last_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div className="flex flex-col mt-4">
                    <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                      Prosecutor Registration Number
                    </label>
                    <input
                      type="text"
                      placeholder="#123456"
                      className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                      name="prosecutor_registration_number"
                      value={formState?.prosecutor_registration_number ?? ""}
                      onChange={handleChange}
                    />
                  </div>
                  {/* Agreement With Suspect */}
                  {/* <div className="flex flex-col mt-4">
                    <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                      Agreement With Suspect
                    </label>
                    <select
                      className="border border-gray-300 rounded-lg bg-[#F2F2F2] p-2 pr-8 focus:outline-none focus:ring focus:ring-blue-300"
                      name="agreement_with_suspect"
                      value={
                        formState?.agreement_with_suspect ? "true" : "false"
                      }
                      onChange={(e) =>
                        setFormState({
                          ...formState,
                          agreement_with_suspect: e.target.value === "true",
                        })
                      }
                    >
                      <option value="false">No</option>
                      <option value="true">Yes</option>
                    </select>
                  </div> */}
                  <div className="flex flex-col mt-4">
                    <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                      Request Date
                    </label>
                    <input
                      type="date"
                      className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                      name="request_date"
                      value={
                        formState?.request_date
                          ? typeof formState.request_date === "string"
                            ? formState.request_date.substring(0, 10)
                            : ""
                          : ""
                      }
                      onChange={handleChange}
                    />
                  </div>

                  <div className="flex flex-col mt-4 col-span-2">
                    <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                      Conversion Summary
                    </label>
                    <textarea
                      className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                      name="conversion_summary"
                      value={formState?.conversion_summary ?? ""}
                      onChange={handleChange}
                      rows={4}
                    />
                  </div>
                </div>
                <div className="flex flex-col mt-4 col-span-2">
                  <FileUpload
                    label="Agreement With Suspect File"
                    fileTypes="*"
                    required={true}
                    onChange={(file) =>
                      handleFile("agreement_with_suspect_file", file)
                    }
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 flex justify-between gap-2">
            <button
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded w-full hover:bg-gray-300"
              onClick={onCancel}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`border border-[#E90101] bg-[#E90101] px-10 py-2 text-white 
                              font-[Poppins] cursor-pointer font-[500] w-full
                              text-base rounded-md hover:bg-white hover:text-[#E90101]
                              ${
                                loading ? "opacity-50 cursor-not-allowed" : ""
                              }`}
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  {LoadingIcon}
                  Loading...
                </div>
              ) : (
                "Create"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RequestSuspectToWitnessModal;
